import { INumpadConfig, INumpadResult } from '@/types/components';
import { CashRegister, I18n, IOrderPaymentValue, IPaymentType, ITenant, Money, Order, OrderItem, PaymentTypeLabel, Store, TenantService } from 'app-core'
import i18n from '@/i18n'
import { inject } from 'vue';
import { useToast } from '@/composables/useToast';

const t = i18n.global.t
const { toast } = useToast()
let openNumpad: (config: INumpadConfig) => void = () => {}

export function addPayments(price: Money, paymentTypes: IPaymentType[]): Promise<IOrderPaymentValue[]> {
  const precision = 2
  const toPay = price.toRoundedUnit(precision)

  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.mixed'),
      okButtonEnabled: (amount, inputValues) => {
        const totalPaid = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
          .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

        return totalPaid.toUnit() === toPay
      },
      amountDisabled: true,
      signButtonDisabled: true,
      maxSum: toPay,
      precision,
      initialValue: 0,
      inputs: paymentTypes.map((paymentType, index) => {
        return {
          type: 'number',
          label: paymentType.display,
          value: 0,
          mutator: (amount, inputValues) => {
            const curValue = inputValues[index]
            if(curValue !== 0) return curValue

            const totalPaid = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
              .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

            return Money.parse(toPay).subtract(totalPaid).toUnit()
          },
          mutateOn: 'focus',
        }
      }),
      labels: [
        { content: (amount, inputValues) => [ t('general.to_pay') + ':', toPay.toFixed(precision) ] },
        { content: (amount, inputValues) => {
          const totalPaid = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
            .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

          const remaining = Money.parse(toPay).subtract(totalPaid).toUnit()
          return [ t('general.remaining') + ':', remaining.toFixed(precision) ]
        } },
      ],
      cancelHandler: () => resolve([]),
      confirmHandler: (data: INumpadResult) => {
        const payments: IOrderPaymentValue[] = data.inputs.map((input, index) => {
          return {
            key: paymentTypes[index].key,
            display: paymentTypes[index].display,
            amount: input.value as number
          }
        }).filter(i => i.amount > 0)

        resolve(payments)
      }
    }

    openNumpad(config)
  })
}

export function addInstallments(price: Money): Promise<IOrderPaymentValue[]> {
  const precision = 2
  const toPay = price.toRoundedUnit(precision)

  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.installments'),
      amountLabel: t('general.other_noncash_initial_cash'),
      okButtonEnabled: (amount, inputValues) => {
        const inputsSum = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
          .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

        const totalPaid = Money.parse(amount).add(inputsSum)
        return totalPaid.toUnit() === toPay
      },
      mutator: (amount, inputValues) => {
        const totalPaid = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
          .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

        return Money.parse(toPay).subtract(totalPaid).toUnit()
      },
      mutateOn: 'auto',
      signButtonDisabled: true,
      max: toPay,
      precision: precision,
      initialValue: 0,
      inputs: [{
        type: 'number',
        label: t('general.other_noncash_remaining'),
        max: toPay,
        value: 0,
        mutator: (amount, inputValues) => {
          if(amount >= toPay) return 0
          return Money.parse(toPay).subtract(Money.parse(amount)).toUnit()
        },
        mutateOn: 'auto',
      }],
      cancelHandler: () => resolve([]),
      confirmHandler: (data: INumpadResult) => {
        const payments: IOrderPaymentValue[] = [{
          key: PaymentTypeLabel.cash,
          display: t('orders.payment_types.cash'),
          amount: data.amount
        },{
          key: PaymentTypeLabel.other_noncash,
          display: t('orders.payment_types.other_noncash'),
          amount: data.inputs[0].value as number
        }].filter(p => p.amount > 0)

        resolve(payments)
      }
    }

    openNumpad(config)
  })
}

export function addCashPayment(price: Money): Promise<IOrderPaymentValue | undefined> {
  const precision = 2
  const toPay = price.toRoundedUnit(precision)

  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.cash'),
      okButtonEnabled: (amount, inputValues) => Money.parse(amount).toUnit() >= toPay && Money.parse(amount).toUnit() <= toPay + 100000,
      signButtonDisabled: true,
      precision: precision,
      initialValue: toPay,
      labels: [
        {
          // The change is what the cashier reads off the screen.
          emphasis: 'prominent',
          content: (amount) => {
            const change = Money.parse(amount).subtract(Money.parse(toPay)).toUnit()
            return [
              t('general.change') + ':', change > 0 ? change.toFixed(precision) : '0.00'
            ]
          },
        },
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => {
        resolve({
          key: PaymentTypeLabel.cash,
          display: t('orders.payment_types.cash'),
          amount: data.amount as number
        })
      }
    }

    openNumpad(config)
  })
}

export function addCreditCardPayment(price: Money): Promise<IOrderPaymentValue[]> {
  const precision = 2
  const toPay = price.toRoundedUnit(precision)

  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: `${t('general.credit_card')} / ${t('general.cash')}`,
      amountLabel: t('general.credit_card'),
      okButtonEnabled: (amount, inputValues) => amount >= 0 && amount <= toPay * 20,
      mutator: (amount, inputValues) => {
        const totalPaid = inputValues.filter(val => typeof val === 'number' && !isNaN(val))
          .reduce((acc, cur) => acc.add(Money.parse(cur as number)), Money.parse(0))

        return Money.parse(toPay).subtract(totalPaid).toUnit()
      },
      mutateOn: 'auto',
      signButtonDisabled: true,
      precision: precision,
      initialValue: toPay,
      inputs: [{
        type: 'number',
        label: t('general.cash'),
        max: toPay,
        value: 0,
        mutator: (amount, inputValues) => {
          if(amount >= toPay) return 0
          return Money.parse(toPay).subtract(Money.parse(amount)).toUnit()
        },
        mutateOn: 'auto',
      }],
      labels: [
        { content: (amount, inputValues) => {
          if(amount <= toPay) return ['']
          return [ t('general.tip') + ':', Money.parse(amount).subtract(Money.parse(toPay)).toUnit().toFixed(precision) ]
        } }
      ],
      cancelHandler: () => resolve([]),
      confirmHandler: (data: INumpadResult) => {
        const cardPayment = {
          key: PaymentTypeLabel.credit_card,
          display: t('orders.payment_types.credit_card'),
          amount: data.amount
        }

        const cashPayment = {
          key: PaymentTypeLabel.cash,
          display: t('orders.payment_types.cash'),
          amount: data.inputs[0].value as number
        }

        resolve([
          cardPayment,
          ...(cashPayment.amount > 0 ? [cashPayment] : [])
        ])
      }
    }

    openNumpad(config)
  })
}

export function addUserDeposit(currentDeposit: number): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.deposit'),
      signButtonDisabled: true,
      precision: 2,
      initialValue: 0,
      labels: [
        { content: (amount, inputValues) => [ t('general.deposit_current') + ': ', currentDeposit.toFixed(2) ] },
        { content: (amount, inputValues) => [ t('general.deposit_new') + ': ', Money.parse(amount).add(Money.parse(currentDeposit)).toFormat('0.00') ] },
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function addOrderDiscount(orders: Order[]): Promise<number | undefined> {
  const mapOrders = (amount: number) => orders.map(o => o.$duplicate()).map(o => {
    o.giveDiscount(amount)
    return o
  })

  let initialValue = 0
  const uniqueDiscounts = orders.flatMap(o => o.items)
    .map(item => item.getDiscountPercent())
    .filter((val, index, array) => index === array.indexOf(val))

  if(uniqueDiscounts.length === 1) {
    initialValue = uniqueDiscounts[0]
  }

  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('order.discount'),
      signButtonDisabled: true,
      precision: 2,
      max: 99,
      initialValue: initialValue,
      suffix: '%',
      labels: [
        { content: (amount, inputValues) => {
          const price = mapOrders(amount).reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0))
          return [ t('general.price') + ': ', price.toFormat('0.00') ]
        } },
        { content: (amount, inputValues) => {
          const discountAmount = mapOrders(amount).reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0))
          return [ t('general.discount') + ': ', discountAmount.toFormat('0.00') ]
        } },
        { content: (amount, inputValues) => {
          const toPay = mapOrders(amount).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0))
          return [ t('general.to_pay') + ': ', toPay.toFormat('0.00') ]
        } },
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function addOrderItemDiscount(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.discount_item'),
      signButtonDisabled: true,
      precision: 2,
      max: 99,
      initialValue: item.getDiscountPercent(),
      suffix: '%',
      labels: [
        { content: (amount, inputValues) => {
          const i = item.$duplicate()
          i.giveDiscount(amount)
          return [ t('general.price') + ': ', i.getPrice().toFormat('0.00') ]
        } },
        { content: (amount, inputValues) => {
          const i = item.$duplicate()
          i.giveDiscount(amount)
          return [ t('general.discount') + ': ', i.getDiscountAmount().toFormat('0.00') ]
        } },
        { content: (amount, inputValues) => {
          const i = item.$duplicate()
          i.giveDiscount(amount)
          return [ t('general.to_pay') + ': ', i.getToPay().toFormat('0.00') ]
        } },
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function setOrderItemQuantity(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.quantity'),
      okButtonEnabled: (amount, inputValues) => amount !== 0,
      signButtonDisabled: !I18n.c('order.allowItemNegativeQuantity'),
      precision: 3,
      initialValue: item.getCount(),
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function setProductQuantity(): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.quantity'),
      okButtonEnabled: (amount, inputValues) => amount !== 0,
      signButtonDisabled: true,
      precision: 3,
      initialValue: 1,
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function setOrderItemPrice(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.price'),
      signButtonDisabled: true,
      precision: 3,
      initialValue: item.getUnitPrice().toRoundedUnit(3),
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function addCashRegisterDeposit(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const curCashRegister = CashRegister.current()
    if(!curCashRegister) {
      resolve(false)
      return
    }

    // An inactive tenant makes no deposit (CashRegister.addTransaction refuses it), and this prompt —
    // undismissable while a deposit is due — would open above the account lock that says why.
    if(TenantService.isInactive(Store.get<ITenant>('tenant'))) {
      resolve(false)
      return
    }

    const currentCash = curCashRegister.getCash().toUnit()

    const config: INumpadConfig = {
      title: t('general.cash_register_add_deposit'),
      signButtonDisabled: true,
      precision: 2,
      initialValue: 0,
      canDismiss: !curCashRegister.depositRequired(),
      labels: [
        { content: (amount, inputValues) => [ t('general.cash_current') + ': ', currentCash.toFixed(2) ] },
        { content: (amount, inputValues) => [ t('general.cash_new') + ': ', Money.parse(currentCash).add(Money.parse(amount)).toFormat('0.00') ] },
      ],
      cancelHandler: () => resolve(false),
      confirmHandler: async (data: INumpadResult) => {
        const result = await curCashRegister.addTransaction(data.amount)

        if(!result?.success) {
          toast(t('general.cash_register_add_deposit_error'), 'error')
          resolve(false)
          return
        }

        toast(t('general.cash_register_add_deposit_success'), 'success')

        resolve(true)
      },
    }

    openNumpad(config)
  })
}

export function addCashRegisterCashOut(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const curCashRegister = CashRegister.current()
    if(!curCashRegister) {
      resolve(false)
      return
    }

    const currentCash = curCashRegister.getCash().toUnit()

    const config: INumpadConfig = {
      title: t('general.cash_register_cash_out'),
      signButtonDisabled: true,
      precision: 2,
      initialValue: 0,
      max: currentCash,
      labels: [
        { content: (amount, inputValues) => [ t('general.cash_current') + ': ', currentCash.toFixed(2) ] },
        { content: (amount, inputValues) => [ t('general.cash_new') + ': ', Money.parse(currentCash).subtract(Money.parse(amount)).toFormat('0.00') ] },
      ],
      cancelHandler: () => resolve(false),
      confirmHandler: async (data: INumpadResult) => {
        const result = await curCashRegister.addTransaction(-1 * data.amount)

        if(!result?.success) {
          toast(t('general.cash_out_error'), 'error')
          resolve(false)
          return
        }

        toast(t('general.cash_out_success'), 'success')

        resolve(true)
      },
    }

    openNumpad(config)
  })
}

export function orderItemRefundCount(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const precision = 3

    const config: INumpadConfig = {
      title: t('general.refund'),
      amountLabel: `${t('general.refund_count')}<br>(${t('general.total').toLocaleLowerCase()} ${item.getCount()})`,
      signButtonDisabled: true,
      precision: precision,
      initialValue: 0,
      max: item.getCount(),
      labels: [
        { content: (amount, inputValues) => {
          if(amount === 0) return []

          const i = item.$duplicate()
          i.updateAdditionalCount(amount);
          return [ t('general.refund') + ': ', i.getAdditionalToPay().abs().toFormat() ]
        } }
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function creditNoteRefundCount(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const precision = 3

    const config: INumpadConfig = {
      title: t('general.refund'),
      amountLabel: `${t('general.return_count')}<br>(${t('general.total').toLocaleLowerCase()} ${item.getCount()})`,
      signButtonDisabled: true,
      precision: precision,
      initialValue: 0,
      max: item.getCount(),
      labels: [
        { content: (amount, inputValues) => {
          if(amount === 0) return []

          const i = item.$duplicate()
          i.updateAdditionalCount(amount);
          return [ t('general.refund') + ': ', i.getAdditionalPriceBeforeTax().abs().toFormat() ]
        } }
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function creditNoteAdditionalDiscount(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const precision = 2

    const config: INumpadConfig = {
      title: t('general.credit_note'),
      amountLabel: t('general.additional_discount'),
      signButtonDisabled: true,
      precision: precision,
      initialValue: 0,
      max: 99,
      suffix: '%',
      labels: [
        { content: (amount, inputValues) => {
          if(amount === 0) return []

          const i = item.$duplicate()
          i.giveAdditionalDiscount(amount)
          return [ t('general.credit_note_amount') + ': ', i.getAdditionalPriceBeforeTax().abs().toFormat() ]
        } }
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function creditNoteAdditionalDiscountAmount(item: OrderItem): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const precision = 2

    const config: INumpadConfig = {
      title: t('general.credit_note'),
      amountLabel: t('general.credit_note_amount'),
      signButtonDisabled: true,
      precision: precision,
      initialValue: 0,
      max: item.getPriceBeforeTax().toRoundedUnit(precision),
      labels: [
        { content: (amount, inputValues) => {
          if(amount === 0) return []

          const discountPercent = Money.parse(amount * 100)
            .divide(item.getPriceBeforeTax().toUnit())
            .toRoundedUnit(2)

          return [ t('general.additional_discount') + ': ', discountPercent + '%' ]
        } }
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => {
        const discountPercent = Money.parse(data.amount * 100)
          .divide(item.getPriceBeforeTax().toUnit())
          .toRoundedUnit(2)

        resolve(discountPercent)
      }
    }

    openNumpad(config)
  })
}

export function addClientInstallment(debt: number): Promise<number | undefined> {
  return new Promise((resolve, reject) => {
    const config: INumpadConfig = {
      title: t('general.installments_pay'),
      okButtonEnabled: (amount, inputValues) => amount > 0,
      signButtonDisabled: true,
      precision: 2,
      initialValue: 0,
      max: debt,
      labels: [
        { content: (amount, inputValues) => [ t('general.installments_remaining') + ': ', Money.parse(debt).subtract(Money.parse(amount)).toFormat('0.00') ] },
      ],
      cancelHandler: () => resolve(undefined),
      confirmHandler: (data: INumpadResult) => resolve(data.amount)
    }

    openNumpad(config)
  })
}

export function useNumpad() {
  openNumpad = inject('openNumpad') as (config: INumpadConfig) => void

  return {
    addPayments,
    addInstallments,
    addCashPayment,
    addCreditCardPayment,
    addUserDeposit,
    addOrderDiscount,
    addOrderItemDiscount,
    setOrderItemQuantity,
    setProductQuantity,
    setOrderItemPrice,
    addCashRegisterDeposit,
    addCashRegisterCashOut,
    orderItemRefundCount,
    creditNoteRefundCount,
    creditNoteAdditionalDiscount,
    creditNoteAdditionalDiscountAmount,
    addClientInstallment
  }
}
